import React, { useState, useEffect } from "react";
import { ChevronUpIcon, ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { BookOpenIcon, DocumentTextIcon, QuestionMarkCircleIcon, ChatBubbleLeftRightIcon, SparklesIcon } from "@heroicons/react/24/solid";

interface SupportChatWidgetProps {
  onBookDemo: () => void;
  onOpenDocs: () => void;
  onOpenHelp: () => void;
}

export const SupportChatWidget: React.FC<SupportChatWidgetProps> = ({
  onBookDemo,
  onOpenDocs,
  onOpenHelp,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);

  useEffect(() => {
    if (isOpen && !isInitialized) {
      setIsInitialized(true);
      setTimeout(() => setAnimateIn(true), 100);
    }
  }, [isOpen, isInitialized]);

  useEffect(() => {
    // Auto-show welcome message after 3 seconds
    const timer = setTimeout(() => {
      if (showWelcome) {
        setIsOpen(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [showWelcome]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setShowWelcome(false);
  };

  const handleAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Chat Panel */}
      {isOpen && (
        <div
          className={`absolute bottom-16 right-0 w-96 transform transition-all duration-500 ease-out ${
            animateIn 
              ? "translate-y-0 opacity-100 scale-100" 
              : "translate-y-8 opacity-0 scale-95"
          }`}
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-600 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                    <SparklesIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base">Welcome to Metatalio! 🚀</h3>
                    <p className="text-sm text-slate-200">How can we help you today?</p>
                  </div>
                </div>
                <button
                  onClick={toggleChat}
                  className="text-white/70 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 bg-gray-50 dark:bg-gray-800">
              <div className="space-y-3">
                {/* Book a Demo */}
                <button
                  onClick={() => handleAction(onBookDemo)}
                  className="w-full group bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-xl p-4 text-left transition-all duration-200 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-500"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-11 h-11 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center text-slate-700 dark:text-slate-300 group-hover:bg-slate-200 dark:group-hover:bg-slate-600 transition-colors">
                      <BookOpenIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">Book a Demo ✨</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        See Metatalio's AI magic in action
                      </p>
                    </div>
                    <ChevronUpIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transform group-hover:rotate-90 transition-all duration-200" />
                  </div>
                </button>

                {/* Documentation */}
                <button
                  onClick={() => handleAction(onOpenDocs)}
                  className="w-full group bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-xl p-4 text-left transition-all duration-200 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-500"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-11 h-11 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center text-slate-700 dark:text-slate-300 group-hover:bg-slate-200 dark:group-hover:bg-slate-600 transition-colors">
                      <DocumentTextIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">Documentation 📚</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Complete guides & tutorials
                      </p>
                    </div>
                    <ChevronUpIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transform group-hover:rotate-90 transition-all duration-200" />
                  </div>
                </button>

                {/* Help Center */}
                <button
                  onClick={() => handleAction(onOpenHelp)}
                  className="w-full group bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-xl p-4 text-left transition-all duration-200 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-500"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-11 h-11 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center text-slate-700 dark:text-slate-300 group-hover:bg-slate-200 dark:group-hover:bg-slate-600 transition-colors">
                      <QuestionMarkCircleIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">Help Center 🤝</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        FAQs & instant answers
                      </p>
                    </div>
                    <ChevronUpIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transform group-hover:rotate-90 transition-all duration-200" />
                  </div>
                </button>
              </div>

              {/* Contact Info */}
              <div className="mt-6 p-4 bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
                  Need more help? Contact us at{" "}
                  <a
                    href="mailto:support@metatalio.com"
                    className="text-slate-700 dark:text-slate-300 hover:underline font-medium"
                  >
                    support@metatalio.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Button */}
      <button
        onClick={toggleChat}
        className={`relative w-16 h-16 bg-slate-700 hover:bg-slate-600 text-white rounded-full shadow-xl transition-all duration-300 hover:shadow-2xl hover:scale-105 flex items-center justify-center group ${
          isOpen ? "rotate-180" : ""
        }`}
      >
        {/* Icon */}
        <div className="relative z-10">
          {isOpen ? (
            <ChevronDownIcon className="w-6 h-6" />
          ) : (
            <ChatBubbleLeftRightIcon className="w-6 h-6" />
          )}
        </div>
        
        {/* Notification dot */}
        {showWelcome && !isOpen && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
        )}
      </button>
    </div>
  );
};